import type {
  ComponentPropsWithoutRef,
  ElementType,
  HTMLAttributes,
} from "react";

import { cn } from "@src/shared/lib/utils";
import {
  type DisplayErrorMessageOptions,
  getDisplayErrorMessage,
} from "@src/shared/utils/errors";

type ErrorMessageSizing = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

const sizingClass: Record<ErrorMessageSizing, string> = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
};

interface ErrorMessageOwnProps extends DisplayErrorMessageOptions {
  error?: unknown;
  sizing?: ErrorMessageSizing;
  variant?: "plain" | "boxed";
  className?: string;
  role?: HTMLAttributes<HTMLElement>["role"];
  "aria-live"?: HTMLAttributes<HTMLElement>["aria-live"];
}

export type ErrorMessageProps<As extends ElementType = "span"> =
  ErrorMessageOwnProps & {
    as?: As;
  } & Omit<
      ComponentPropsWithoutRef<As>,
      keyof ErrorMessageOwnProps | "as" | "children"
    >;

export function ErrorMessage<As extends ElementType = "span">({
  as,
  error,
  fallback,
  notFoundMessage,
  statusMessages,
  variant = "plain",
  sizing = "sm",
  className,
  role = "alert",
  "aria-live": ariaLive = "polite",
  ...componentProps
}: ErrorMessageProps<As>) {
  const message = getDisplayErrorMessage(error, {
    fallback,
    notFoundMessage,
    statusMessages,
  });

  if (!message) return null;

  const Component = as ?? "span";

  return (
    <Component
      aria-live={ariaLive}
      className={cn(
        "text-destructive leading-relaxed",
        sizingClass[sizing],
        variant === "boxed" &&
          "rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-left",
        className,
      )}
      role={role}
      {...componentProps}
    >
      {message}
    </Component>
  );
}
